export const MEDICAL_MODEL = 'gpt-4.1';

const SYSTEM_PROMPT = `You explain medical reports to Australian patients in plain language.
You are an educational assistant, not a doctor, and must not diagnose, prescribe, or replace
professional care. Analyse only information explicitly present in the supplied report.

Requirements:
- Never invent values, findings, symptoms, diagnoses, reference ranges, or patient history.
- Clearly distinguish reported facts from general educational context.
- State when text is unclear, incomplete, or may contain OCR errors.
- Do not describe any result as definitively safe, dangerous, normal, or abnormal unless the
  report itself supplies a reference range that supports that description.
- For potentially urgent findings, advise prompt review by a qualified clinician. If the report
  or user content indicates an immediate emergency, advise calling 000 in Australia.
- Encourage the user to confirm every interpretation with their treating clinician.
- Do not provide medication changes, treatment instructions, or personalised diagnoses.

Use these headings:
1. Summary of what the report says
2. Results explained in plain language
3. Items to discuss with a healthcare professional
4. Suggested questions for the next appointment
5. Important limitations`;

const getErrorMessage = (status, error) => {
  if (status === 401) {
    return 'OpenAI rejected the API key. Check the key and try again.';
  }

  if (status === 429) {
    return 'OpenAI rate or billing limits were reached. Check the OpenAI account and try again.';
  }

  return error?.message || `OpenAI returned an error (${status}).`;
};

export const analyseMedicalText = async ({
  text,
  apiKey,
  sectionNumber,
  sectionCount
}) => {
  const sectionContext = sectionCount > 1
    ? `This is section ${sectionNumber} of ${sectionCount}. Explain only this section and avoid a final whole-document conclusion.`
    : 'This is the complete extracted document.';

  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      model: MEDICAL_MODEL,
      messages: [
        {
          role: 'system',
          content: `${SYSTEM_PROMPT}\n\n${sectionContext}`
        },
        {
          role: 'user',
          content: `Explain the following extracted medical-report text:\n\n${text}`
        }
      ],
      max_completion_tokens: 3500,
      temperature: 0.1
    })
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(getErrorMessage(response.status, payload.error));
  }

  const payload = await response.json();
  const content = payload.choices?.[0]?.message?.content?.trim();

  if (!content) {
    throw new Error('OpenAI returned an empty analysis. Please try again.');
  }

  return content;
};